import { defineProperty as _defineProperty, slicedToArray as _slicedToArray, asyncToGenerator as _asyncToGenerator, regeneratorRuntime as _regeneratorRuntime } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import { useAsyncList, Combobox, ComboboxSearchInput, ComboboxPopover, ComboboxList, ComboboxSection, ComboboxOption, Link } from '@vp/swan';
import React__default, { useState, useMemo, useEffect } from 'react';
import localize from '../../../util/localize.js';
import { POPULAR_SEARCH_QUERY_SUBMITTED_LABEL, RECENT_SEARCH_QUERY_SUBMITTED_LABEL, SEARCH_BAR_ENGAGED_EVENT_FOCUS_LABEL, SEARCH_BAR_ENGAGED_EVENT_TYPED_LABEL, SEARCH_RESULT_CLICKED } from '../../../util/analyticsConstants.js';
import { useRecentSearch } from '../../../localStorage/useRecentSearch.js';
import { useSearchContext } from '../../../searchContext/SearchContextProvider.js';
import { PRODUCT_SEARCH_ON_FOCUS_CONTENT_VARIATION_TEST, PRODUCT_SEARCH_ON_FOCUS_CONTENT, PRODUCT_SEARCH_QUERY_SUGGESTIONS_VARIATION, PRODUCT_SEARCH_QUERY_SUGGESTIONS_CONTROL, PRODUCT_SEARCH_QUERY_SUGGESTIONS_EXPERIMENT } from '../../../util/abTestConstants.js';
import { SuggestedQueriesTaxonomy, RecentSearchesTaxonomy, PopularSearchesTaxonomy, CategoriesResultTaxonomy, ProductResultTaxonomy } from '../../taxonomy/Taxonomies.js';
import { generateResultSections } from './querySuggestionItemsCalculator.js';
import { useTracking } from './QuerySuggestionsSearchInputTrackingProvider.js';
import debounce from 'lodash.debounce';
import QuerySuggestionsRecentSearchItem from './QuerySuggestionsSuggestedQueryItem.js';
import QuerySuggestionsSearchResultItem from './QuerySuggestionsSearchResultItem.js';
import { getDestinationUrl } from '../../../util/destinationUrlBuilder.js';
import { usePopularSearches } from '../../../searchContext/usePopularSearches.js';
import { getSuggestedQueries } from '../../../clients/algoliaClient.js';
import { convertSuggestedQueriesResponseToTrackingInput, convertSuggestedQueriesResponseToComboboxItems } from '../../../services/algoliaService.js';

var QUERY_SUBMITTED_LABELS = _defineProperty(_defineProperty(_defineProperty({}, RecentSearchesTaxonomy.id, 'Recent Search'), PopularSearchesTaxonomy.id, 'Popular Search'), SuggestedQueriesTaxonomy.id, 'Suggested Search');
var QuerySuggestionsSearchInput = function QuerySuggestionsSearchInput(props) {
  var locale = props.locale,
    query = props.query,
    searchLabel = props.searchLabel,
    searchPlaceholder = props.searchPlaceholder;
    props.logger;
    var isCustomerOnQuerySuggestionsExperiment = props.isCustomerOnQuerySuggestionsExperiment;
  var _useState = useState(query || ''),
    _useState2 = _slicedToArray(_useState, 2),
    currentQuery = _useState2[0],
    setCurrentQuery = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    searchTypedEngaged = _useState4[0],
    setSearchTypedEngaged = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    searchFocusEngaged = _useState6[0],
    setSearchFocusEngaged = _useState6[1];
  var _useRecentSearch = useRecentSearch(),
    recentSearches = _useRecentSearch.recentSearches,
    addRecentSearch = _useRecentSearch.addRecentSearch,
    clearRecentSearches = _useRecentSearch.clearRecentSearches,
    isRecentSearchesLoaded = _useRecentSearch.isRecentSearchesLoaded;
  var _useSearchContext = useSearchContext(),
    fireImpression = _useSearchContext.fireImpression,
    getResultsAsync = _useSearchContext.getResultsAsync,
    resultsPageUrl = _useSearchContext.resultsPageUrl;
  var comboRef = React__default.useRef(null);
  var inputRef = React__default.useRef(null);
  var _useTracking = useTracking(),
    updateQuery = _useTracking.updateQuery,
    sendQuerySubmittedEvent = _useTracking.sendQuerySubmittedEvent,
    sendSearchBarEngagedEvent = _useTracking.sendSearchBarEngagedEvent,
    sendSearchResultClickEvent = _useTracking.sendSearchResultClickEvent,
    sendSearchResultSectionEvent = _useTracking.sendSearchResultSectionEvent,
    isTrackingInitialized = _useTracking.isTrackingInitialized;
  var _usePopularSearches = usePopularSearches(locale),
    popularSearches = _usePopularSearches.popularSearches,
    isPopularSearchesLoaded = _usePopularSearches.isPopularSearchesLoaded;
  var handleFocus = function handleFocus(_) {
    if (isTrackingInitialized() && !searchFocusEngaged) {
      sendSearchBarEngagedEvent(SEARCH_BAR_ENGAGED_EVENT_FOCUS_LABEL);
      setSearchFocusEngaged(true);
      fireImpression({
        Experiment: PRODUCT_SEARCH_ON_FOCUS_CONTENT,
        Variation: PRODUCT_SEARCH_ON_FOCUS_CONTENT_VARIATION_TEST
      });
    }
  };
  function setFilterText(value) {
    queryResults.setFilterText(value);
  }
  var debouncedSetFilterText = useMemo(function () {
    var debounced = debounce(function (value) {
      setFilterText(value);
    }, 200, {
      maxWait: 1000
    });
    return debounced;
  }, [popularSearches, recentSearches]);
  useEffect(function () {
    return function () {
      debouncedSetFilterText.cancel();
    };
  }, [debouncedSetFilterText]);
  useEffect(function () {
    var _document;
    var params = ((_document = document) === null || _document === void 0 ? void 0 : _document.location.search) && new URLSearchParams(document.location.search);
    var query = params ? (params === null || params === void 0 ? void 0 : params.has('query')) && (params === null || params === void 0 ? void 0 : params.get('query')) : '';
    if (query) {
      setCurrentQuery(query);
      setFilterText(query);
    }
  }, []);
  useEffect(function () {
    if (!isPopularSearchesLoaded || !isRecentSearchesLoaded) {
      return;
    }
    queryResults === null || queryResults === void 0 || queryResults.reload();
  }, [JSON.stringify(recentSearches), JSON.stringify(popularSearches), isPopularSearchesLoaded, isRecentSearchesLoaded]);
  function handleInputChange(value) {
    if (isTrackingInitialized() && !searchTypedEngaged) {
      sendSearchBarEngagedEvent(SEARCH_BAR_ENGAGED_EVENT_TYPED_LABEL);
      setSearchTypedEngaged(true);
      fireImpression({
        Experiment: PRODUCT_SEARCH_QUERY_SUGGESTIONS_EXPERIMENT,
        Variation: isCustomerOnQuerySuggestionsExperiment ? PRODUCT_SEARCH_QUERY_SUGGESTIONS_VARIATION : PRODUCT_SEARCH_QUERY_SUGGESTIONS_CONTROL
      });
    }
    setCurrentQuery(value);
    if (isRemovingCharacters(value)) {
      setFilterText(value);
    } else {
      debouncedSetFilterText(value);
    }
    updateQuery(value);
  }
  var placeholder = searchPlaceholder || localize('searchPlaceholder', locale);
  function handleSubmit(event) {
    event.preventDefault();
    if (currentQuery) {
      addRecentSearch(currentQuery);
      sendQuerySubmittedEvent({});
    }
    var destinationUrl = "".concat(resultsPageUrl, "?query=").concat(currentQuery);
    document.location.href = destinationUrl;
  }
  function handleSelectedItem(selectedItem) {
    if (selectedItem === 'clearAllRecentSearches') {
      clearRecentSearches();
      queryResults.reload();
      return;
    }
    var searchSelectedItem = getSearchSelectedItemFromQueryItems(queryResults, selectedItem);
    if (searchSelectedItem.isSuggestedQuery) {
      var _comboRef$current, _inputRef$current;
      sendQuerySubmittedEvent({
        label: searchSelectedItem.trackingLabel,
        queryToOverride: searchSelectedItem.title,
        searchTerm: currentQuery
      });

      // We close the flyout before redirecting, so the customer does not see an update on recent searches before
      (_comboRef$current = comboRef.current) === null || _comboRef$current === void 0 || _comboRef$current.blur();
      (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 || _inputRef$current.blur();
      setTimeout(function () {
        return addRecentSearch(searchSelectedItem.title);
      }, 100);
      document.location.href = "".concat(resultsPageUrl, "?query=").concat(searchSelectedItem.title);
      return;
    }
    sendSearchResultClickEvent({
      eventName: SEARCH_RESULT_CLICKED,
      destinationUrl: getDestinationUrl({
        landingUrl: searchSelectedItem.landingUrl,
        query: queryResults.filterText
      }),
      searchItem: searchSelectedItem
    });
    document.location.href = getDestinationUrl({
      landingUrl: searchSelectedItem.landingUrl,
      query: queryResults.filterText
    });
    return;
  }
  var queryResults = useAsyncList({
    load: function load(_ref) {
      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee() {
        var _document$querySelect, _categories$response$, _products$response$se;
        var filterText, _items, _response, searchResultTrackingDetails, response, categories, products, pageName, itemsToShowCalculation, items;
        return _regeneratorRuntime().wrap(function _callee$(_context) {
          while (1) switch (_context.prev = _context.next) {
            case 0:
              _ref.signal, filterText = _ref.filterText;
              if (filterText) {
                _context.next = 10;
                break;
              }
              if (!(recentSearches.length === 0 && popularSearches.length === 0)) {
                _context.next = 4;
                break;
              }
              return _context.abrupt("return", {
                items: []
              });
            case 4:
              _items = [];
              if (!(recentSearches.length > 0)) {
                _context.next = 8;
                break;
              }
              _items.push({
                name: localize('recentSearches', locale),
                taxonomyId: RecentSearchesTaxonomy.id,
                items: recentSearches.map(function (search) {
                  return {
                    objectID: "recentSearch_".concat(search),
                    title: search,
                    isSuggestedQuery: true,
                    trackingLabel: RECENT_SEARCH_QUERY_SUBMITTED_LABEL,
                    highlightResult: search
                  };
                })
              });
              return _context.abrupt("return", {
                items: _items
              });
            case 8:
              if (popularSearches.length > 0) {
                _items.push({
                  name: localize('popularSearches', locale),
                  taxonomyId: PopularSearchesTaxonomy.id,
                  items: popularSearches.map(function (search) {
                    return {
                      objectID: "popularSearch_".concat(search),
                      title: search,
                      isSuggestedQuery: true,
                      trackingLabel: POPULAR_SEARCH_QUERY_SUBMITTED_LABEL,
                      highlightResult: search
                    };
                  })
                });
              }
              return _context.abrupt("return", {
                items: _items
              });
            case 10:
              if (!isCustomerOnQuerySuggestionsExperiment) {
                _context.next = 17;
                break;
              }
              _context.next = 13;
              return getSuggestedQueries({
                query: filterText,
                tenant: 'Vistaprint',
                environment: 'prod',
                locale: locale
              });
            case 13:
              _response = _context.sent;
              searchResultTrackingDetails = convertSuggestedQueriesResponseToTrackingInput(filterText, _response);
              sendSearchResultSectionEvent(searchResultTrackingDetails);
              return _context.abrupt("return", convertSuggestedQueriesResponseToComboboxItems(filterText, _response, locale));
            case 17:
              _context.next = 19;
              return getResultsAsync(filterText);
            case 19:
              response = _context.sent;
              categories = response[CategoriesResultTaxonomy.id];
              products = response[ProductResultTaxonomy.id];
              pageName = (_document$querySelect = document.querySelector('meta[name="pageName"]')) === null || _document$querySelect === void 0 ? void 0 : _document$querySelect.content;
              itemsToShowCalculation = generateResultSections({
                algoliaResponse: response,
                locale: locale,
                pageName: pageName
              });
              items = itemsToShowCalculation.resultSections;
              sendSearchResultSectionEvent({
                query: filterText,
                taxonomyId: CategoriesResultTaxonomy.id,
                numberOfResultsToShow: itemsToShowCalculation.maxNumberOfCategories,
                indexName: categories.id,
                results: categories.response.results,
                rules: (_categories$response$ = categories.response.sectionTrackingDetails) === null || _categories$response$ === void 0 ? void 0 : _categories$response$.rules
              });
              sendSearchResultSectionEvent({
                query: filterText,
                taxonomyId: ProductResultTaxonomy.id,
                numberOfResultsToShow: itemsToShowCalculation.maxNumberOfProducts,
                indexName: products.id,
                results: products.response.results,
                rules: (_products$response$se = products.response.sectionTrackingDetails) === null || _products$response$se === void 0 ? void 0 : _products$response$se.rules
              });
              return _context.abrupt("return", {
                items: items
              });
            case 28:
            case "end":
              return _context.stop();
          }
        }, _callee);
      }))();
    }
  });
  var isRemovingCharacters = function isRemovingCharacters(value) {
    return !value || queryResults && queryResults.filterText.includes(value);
  };
  return /*#__PURE__*/React__default.createElement(Combobox, {
    maxHeight: 1200 // This value does not matter really as long as it is greater than the height of the list, so, to play safe, we set it to 1200px
    ,
    "aria-label": searchLabel || localize('searchLabel', locale),
    menuTrigger: "focus",
    inputValue: currentQuery,
    onInputChange: handleInputChange,
    onFocus: handleFocus,
    disableAutoFilter: true,
    selectedKey: '',
    ref: comboRef,
    onSelectionChange: function onSelectionChange(selectedItem) {
      if (!selectedItem) {
        return;
      }
      handleSelectedItem(selectedItem);
    },
    allowsCustomValue: true
  }, /*#__PURE__*/React__default.createElement(ComboboxSearchInput, {
    ref: inputRef,
    placeholder: placeholder,
    accessibleTextForSearchButton: placeholder,
    accessibleTextForClearButton: localize('clearLabel', locale),
    onSearchSubmit: handleSubmit,
    onFocusCapture: handleFocus
  }), /*#__PURE__*/React__default.createElement(ComboboxPopover, {
    className: "query-suggestions-search-input-list"
  }, /*#__PURE__*/React__default.createElement(ComboboxList, null, queryResults === null || queryResults === void 0 ? void 0 : queryResults.items.map(function (section, index, self) {
    return /*#__PURE__*/React__default.createElement(ComboboxSection, {
      key: "section_".concat(section.taxonomyId),
      title: section.name
    }, section.items.map(function (searchItem) {
      var _QUERY_SUBMITTED_LABE2;
      return !Object.keys(QUERY_SUBMITTED_LABELS).includes(section.taxonomyId) ? /*#__PURE__*/React__default.createElement(ComboboxOption, {
        key: searchItem.objectID,
        textValue: searchItem.title,
        withThumbnail: true,
        padding: 0
      }, /*#__PURE__*/React__default.createElement(QuerySuggestionsSearchResultItem, {
        key: searchItem.objectID,
        searchItem: searchItem,
        locale: locale,
        query: queryResults.filterText
      })) : /*#__PURE__*/React__default.createElement(ComboboxOption, {
        key: searchItem.objectID,
        padding: 0
      }, /*#__PURE__*/React__default.createElement(QuerySuggestionsRecentSearchItem, {
        searchItem: searchItem,
        trackingEventLabel: (_QUERY_SUBMITTED_LABE2 = QUERY_SUBMITTED_LABELS[section.taxonomy]) !== null && _QUERY_SUBMITTED_LABE2 !== void 0 ? _QUERY_SUBMITTED_LABE2 : ''
      }), ' ');
    }), (section === null || section === void 0 ? void 0 : section.taxonomyId) === RecentSearchesTaxonomy.id && /*#__PURE__*/React__default.createElement(ComboboxOption, {
      key: "clearAllRecentSearches",
      textValue: localize('clearAllRecentSearches', locale),
      fontSize: "small",
      padding: 0,
      paddingLeft: 5,
      py: 4
    }, /*#__PURE__*/React__default.createElement(Link, {
      fontSize: "small"
    }, localize('clearAllRecentSearches', locale))));
  }))));
};
function getSearchSelectedItemFromQueryItems(queryResults, selectedItem) {
  return queryResults.items.flatMap(function (section) {
    return section.items;
  }).find(function (item) {
    return item.objectID === selectedItem;
  });
}

export { QuerySuggestionsSearchInput };
//# sourceMappingURL=QuerySuggestionsSearchInput.js.map
